/**
 * TextField component
 * @author Liwei Yeh <liwei.yeh@adasasistemas.com>
 */

// Library
import { ClassAttributes, InputHTMLAttributes, useMemo } from 'react';
import { FieldHookConfig, useField } from 'formik';
import { isEmpty } from 'lodash';

// CSS Style for this module
import styles from './textFieldFormik.module.scss';

interface InputProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * label of the input element
	 */
	label?: string;
	props: InputHTMLAttributes<HTMLInputElement> &
		ClassAttributes<HTMLInputElement> &
		FieldHookConfig<string>;
	/**
	 * Preset size of the input element. Auto is the default size
	 */
	size?: 'tiny' | 'small' | 'medium' | 'auto';
	/**
	 * Locks the field
	 */
	isLocked?: boolean;
	/**
	 * Text to display if field is locked
	 */
	onLockedText?: string;
	/** Element identifier selector for test */
	testId?: string;
}

/**
 * Text fields let users enter and edit text.
 * @param {InputProps} Prop
 * @returns  an styled  input element
 */
export const TextFieldFormik = ({
	customStyles,
	label,
	size = 'auto',
	isLocked,
	onLockedText,
	testId,
	props,
}: InputProps) => {
	const [field, meta, getFieldHelpers] = useField(props);
	const hasError = useMemo(() => meta.error && meta.touched, [meta.error, meta.touched]);
	const inputStyles = useMemo(
		() =>
			[
				props.type === 'textarea' ? styles.textArea : styles.textField,
				styles[`input-size-${!props.disabled ? size : ''}`],
				styles[`input-disabled-${props.disabled}`],
				styles[`input-${hasError ? 'error' : ''}`],
			].join(' '),
		[props.type, hasError, props.disabled, size],
	);

	const trimValue = (value: string) => {
		getFieldHelpers.setValue(value.trim());
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			{label && <label htmlFor={field.name}>{label}</label>}
			{!props.disabled && !isLocked ? (
				<input
					className={inputStyles}
					{...field}
					{...props}
					onBlur={e => trimValue(e.target.value)}
					data-testid={testId}
				/>
			) : !isEmpty(field.value) ? (
				<p className={inputStyles} data-testid={testId}>
					{isLocked ? onLockedText : field.value}
				</p>
			) : (
				<p className={inputStyles}>&nbsp;</p>
			)}
			{hasError && <p className={styles['error-msg']}>{meta.error}</p>}
		</div>
	);
};
